/** @format */

import LogoDark from '@/assets/joivy-dark.svg';
import LogoYellow from '@/assets/joivy-white.svg';
import LogoJYellow from '@/assets/joivy_logo_j_yellow.svg';
import { CitySelectionDropdown } from '@/components/shared/CitySelectionDropdown';
import '@/components/shared/SearchCity/styles.css';
import { ExternalNavbar } from '@/components/ui/Navbar/ExternalNavbar';
import { MobileBar } from '@/components/ui/Navbar/MobileBar';
import { UISwitchLocale } from '@/components/ui/SwitchLocale';
import { TOtherPaths } from '@/ducks/filters/declarations';
import { GTMheader } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { TTranslation } from '@/i18n/createProvider';
import { useAppTranslation } from '@/i18n/declarations';
import { reduceLocales } from '@/i18n/utils/reduce-locales';
import { NAME_PORTAL_CODE } from '@/utils/gatsby/constants';
import { Link as GatsbyLink } from 'gatsby-plugin-intl';
import React, { forwardRef, memo, useEffect, useMemo, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { RenderPages } from './RenderPages';
import { RenderPagesExt } from './RenderPagesExt';
import {
  Background,
  Container,
  Content,
  LeftContentWrapper,
  LinksContainer,
  LoginLink,
  PagesContainerDesktop,
  VerticalDivider,
} from './index.style';
import { NavbarProps } from './index.types';

const DEFAULT_OTHER_PATHS: TOtherPaths = reduceLocales(l => l);

const Navbar = forwardRef<HTMLDivElement, NavbarProps>(
  ({ page = 'page', className, otherPaths = DEFAULT_OTHER_PATHS, cityName, hidden }, ref) => {
    const { t } = useAppTranslation();
    const [portalCode = NAME_PORTAL_CODE] = useQueryParam('portalCode', StringParam);
    const [isExternalPortalCode, setIsExternalPortalCode] = useState(portalCode !== NAME_PORTAL_CODE);

    useEffect(() => {
      setIsExternalPortalCode(portalCode !== NAME_PORTAL_CODE);
    }, [portalCode]);

    const setGTMlogo = () => GTMheader('logo');

    const Link = isExternalPortalCode ? 'div' : GatsbyLink;

    const enableGreenNavbar = page === 'listing' || page === 'booking';

    const LogoSrc = useMemo(() => {
      if (page === 'home' || enableGreenNavbar) return LogoYellow;
      return LogoDark;
    }, [page]);

    return (
      <Container ref={ref} className={className ?? ''} $hidden={hidden} $page={page}>
        <Background $forceWhite>
          {page != 'booking' && page != 'bookingOld' && page != 'room' && page != 'req-info' && page != 'listing' && (
            <ExternalNavbar page={page} />
          )}
        </Background>
        <Background>
          <Content $listing={page === 'listing'}>
            <>
              <LeftContentWrapper>
                <Link to={isExternalPortalCode ? '' : '/'} onClick={setGTMlogo}>
                  {enableGreenNavbar && (
                    <img
                      src={LogoJYellow}
                      alt="logo"
                      height={enableGreenNavbar ? 32 : 44}
                      className="mobile_listing_logo"
                    />
                  )}
                  <img
                    src={LogoSrc}
                    alt="logo"
                    height={enableGreenNavbar ? 32 : 44}
                    className={enableGreenNavbar ? 'dekstop_listing_logo' : ''}
                  />
                </Link>
                {page === 'listing' && (
                  <>
                    <VerticalDivider />
                    <CitySelectionDropdown isPageListing selectedCity={cityName} />
                  </>
                )}
              </LeftContentWrapper>
              <PagesContainerDesktop>
                <LinksContainer $listing={page === 'listing'}>
                  {isExternalPortalCode ? <RenderPagesExt /> : <RenderPages />}

                  {!isExternalPortalCode && (
                    <LoginLink to="/log-in">
                      <span>{t('log-in.sign-in' as TTranslation)}</span>
                    </LoginLink>
                  )}
                  <UISwitchLocale otherPaths={otherPaths} home={page === 'home' || enableGreenNavbar} />
                </LinksContainer>
              </PagesContainerDesktop>
              <MobileBar otherPaths={otherPaths} page={page} />
            </>
          </Content>
        </Background>
      </Container>
    );
  },
);

export const UINavbar = memo(Navbar);
